import type { NonUndefined } from 'utility-types'

import type { ArrayType } from '../../../../../../utils'

import { Capability, type PaginatedData } from '../../../../../../domain'

import type { PageSubscriptionsTypes, SubscriptionFilterArgs, SubscriptionPageOptions } from '../../../../../../application'

import type { BackendJsonResponseContent, BackendQueryParameters, ServicesDeviationsSupportedType } from '../../../../../Connectware'
import { mapSearch } from '../../../../mappers'
import { createSortMapper, mapPagination, mapPaginationQueryParameters, type SortableColumnsMap } from '../../../mappers'
import { BaseSubscriptionStrategy } from '../Base'

type DeviatedServicesEndpoint = '/api/v2/resources/states/deviations/services'

export type DeviatedServiceResponse = ArrayType<BackendJsonResponseContent<DeviatedServicesEndpoint, 'GET', 200>['data']>

export type DeviatedServicesPageSortableColumnsMap<P extends keyof PageSubscriptionsTypes> =
    /** Force a relationship between the subscription type and the orderBy column */
    SortableColumnsMap<PageSubscriptionsTypes[P], NonUndefined<BackendQueryParameters<DeviatedServicesEndpoint, 'GET'>['orderBy']>>

export abstract class BaseDeviatedServicesStrategy<P extends keyof PageSubscriptionsTypes> extends BaseSubscriptionStrategy<P, never> {
    protected abstract readonly errorMessage: string

    /**
     * Map from domain to backend orderBy columns
     */
    protected abstract sortingColumns: DeviatedServicesPageSortableColumnsMap<P>

    protected abstract deviationsSupportedType: ServicesDeviationsSupportedType

    override readonly supportsPagination = true

    override readonly requiredPermissions = [Capability.DEVIATIONS_READ]

    /**
     * Map responses to domain entities
     */
    protected abstract mapToDomain (response: DeviatedServiceResponse): Promise<PageSubscriptionsTypes[P]>

    override retrievePage ({
        pagination,
        search,
        sort,
    }: SubscriptionFilterArgs & SubscriptionPageOptions<P>): Promise<PaginatedData<PageSubscriptionsTypes[P]>> {
        const mapSort = createSortMapper(this.sortingColumns)

        return this.request<DeviatedServicesEndpoint, 'GET', PaginatedData<PageSubscriptionsTypes[P]>>({
            path: '/api/v2/resources/states/deviations/services',
            method: 'GET',
            capability: Capability.DEVIATIONS_READ,
            authenticate: true,
            queryParams: { ...mapPaginationQueryParameters(pagination), ...mapSort(sort), ...mapSearch(search) },
            handlers: {
                200: async (response) => {
                    const { data, meta } = await response.getJson()

                    const deviated: PageSubscriptionsTypes[P][] = []

                    for (const response of data) {
                        deviated.push(await this.mapToDomain(response))
                    }

                    return { current: deviated, ...mapPagination(meta.pagination) }
                },
                400: BaseDeviatedServicesStrategy.createBadRequestHandler('fetching deviated services'),
                500: BaseDeviatedServicesStrategy.createServerErrorHandler(this.errorMessage),
                503: BaseDeviatedServicesStrategy.createServerNotAvailableHandler('Resources Status Tracking is not running as expected'),
            },
        })
    }

    createAllChangeListener (_: SubscriptionFilterArgs, handler: VoidFunction): Promise<VoidFunction> {
        /**
         * Subscribes to deviations popping up and going away
         */
        return this.rstAdapter.subscribeToServicesDeviationsCount(this.deviationsSupportedType, handler)
    }
}
